import React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { taskV2TimelineScaleAtom } from '../../../../atoms/v3/taskV2Atoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { chatSidebarCollapsedAtom, chatSidebarWidthAtom, layoutModeAtom } from '../../../../atoms/v3/uiAtoms';

interface TimelineScaleControllerProps {
  className?: string;
}

/**
 * 时间轴缩放控制器
 * 悬浮在右下角，允许用户调整时间轴的缩放比例
 * 支持侧栏避让和响应式布局
 */
export const TimelineScaleController: React.FC<TimelineScaleControllerProps> = ({
  className = ''
}) => {
  const [scale, setScale] = useAtom(taskV2TimelineScaleAtom);
  const isDark = useAtomValue(isDarkAtom);
  const chatSidebarCollapsed = useAtomValue(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const { t } = useTranslation(['common']);

  // 缩放范围和步长
  const minScale = 0.5;
  const maxScale = 3.0;
  const step = 0.25;

  // 处理缩放调整
  const handleScaleChange = (delta: number) => {
    const newScale = Math.max(minScale, Math.min(maxScale, scale + delta));
    setScale(newScale);
  };

  // 重置到默认缩放
  const handleReset = () => {
    setScale(1.0);
  };

  // 计算定位样式，参考 IOSBottomContainer 的避让逻辑
  const getPositionStyle = () => {
    if (layoutMode === 'mobile') {
      // 移动端：避让 TabBar + 安全区域
      return {
        position: 'fixed' as const,
        right: '16px',
        bottom: 'calc(8rem + env(safe-area-inset-bottom) + 16px)'
      };
    } else {
      // 桌面端：避让侧边栏
      const rightOffset = chatSidebarCollapsed ? 0 : chatSidebarWidth * 16 - 40; // rem to px
      return {
        position: 'fixed' as const,
        right: `${rightOffset}px`,
        bottom: '90px'
      };
    }
  };

  // 格式化显示文本
  const getScaleText = () => {
    if (scale === 1.0) return '1x';
    return `${scale.toFixed(1)}x`;
  };



  const positionStyle = getPositionStyle();

  return (
    <div
      className={`z-40 ${className}`}
      style={positionStyle}
    >
      {/* 主控制器容器 */}
      <div className={`
        flex flex-col items-center gap-1 p-1.5 rounded-lg backdrop-blur-sm
        ${isDark
          ? 'bg-black/10'
          : 'bg-white/10'
        }
      `}>
        {/* 缩放显示 */}
        <div className="text-center">
          <div className={`text-sm font-medium ${
            isDark ? 'text-white/60' : 'text-black/60'
          }`}>
            {getScaleText()}
          </div>
        </div>

        {/* 控制按钮 */}
        <div className="flex flex-col gap-0.5">
          {/* 放大按钮 */}
          <button
            onClick={() => handleScaleChange(step)}
            disabled={scale >= maxScale}
            className={`
              w-7 h-7 rounded-full flex items-center justify-center text-xs font-normal
              transition-all duration-200 active:scale-95
              ${scale >= maxScale
                ? (isDark ? 'text-white/20' : 'text-black/20')
                : (isDark
                  ? 'text-white/50 hover:text-white/70 hover:bg-white/5'
                  : 'text-black/50 hover:text-black/70 hover:bg-black/5'
                )
              }
            `}
          >
            +
          </button>

          {/* 重置按钮 */}
          <button
            onClick={handleReset}
            disabled={scale === 1.0}
            className={`
              w-7 h-5 rounded flex items-center justify-center text-xs font-normal
              transition-all duration-200 active:scale-95
              ${scale === 1.0
                ? (isDark ? 'text-white/20' : 'text-black/20')
                : (isDark
                  ? 'text-white/40 hover:text-white/60 hover:bg-white/5'
                  : 'text-black/40 hover:text-black/60 hover:bg-black/5'
                )
              }
            `}
          >
            {t('keymap.resetScale', '1x')}
          </button>

          {/* 缩小按钮 */}
          <button
            onClick={() => handleScaleChange(-step)}
            disabled={scale <= minScale}
            className={`
              w-7 h-7 rounded-full flex items-center justify-center text-xs font-normal
              transition-all duration-200 active:scale-95
              ${scale <= minScale
                ? (isDark ? 'text-white/20' : 'text-black/20')
                : (isDark
                  ? 'text-white/50 hover:text-white/70 hover:bg-white/5'
                  : 'text-black/50 hover:text-black/70 hover:bg-black/5'
                )
              }
            `}
          >
            −
          </button>
        </div>


      </div>
    </div>
  );
};
